import { FC, useContext, useEffect, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faInfoCircle, faTimes } from '@fortawesome/free-solid-svg-icons'
import styled from '@emotion/styled'
import cx from 'classnames'

import { HideWarningAction, WarningContext } from 'nexchange-service/module/common'

import config from '@/config'

const { WARNING_TIMEOUT } = config

const Container = styled.div`
    position: fixed;
    top: -100%;
    left: 0;
    width: 100%;
    display: flex;
    justify-content: space-between;
    color: ${props =>
        // @ts-ignore
        props.theme.colors.white};
    background-color: #f1b45c;
    font-size: 0.85rem;
    padding: 1rem 5%;
    transition: top 0.5s ease-out;
    z-index: 9999;

    .warning-close-icon {
        padding: 0.75rem;
        margin: -0.75rem;

        svg {
            width: 1.2rem;
        }
    }

    &.show-warning {
        top: 0;
    }
`

const Message = styled.div`
    display: inline-flex;
    align-items: center;
    text-transform: uppercase;

    .warning-info-icon {
        margin-right: 0.5rem;
        width: 1.2rem;
    }
`

type TWarningRequest = {
    show: boolean
    message: string
    senderId: string
}

class WarningRequest implements TWarningRequest {
    message: string
    senderId: string
    show: boolean

    constructor({ message, senderId }) {
        this.show = true
        this.message = message
        this.senderId = senderId
    }
}

const WARNING_REQUEST_STORAGE_KEY = 'warningRequest'
const warningTriggerHandle = { isTriggered: false }

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
const dispatchWarningRequest = (message, senderId) => {
    if (!window || !window.sessionStorage) {
        throw new Error('window.sessionStorage object is not defined.')
    }

    sessionStorage.setItem(
        WARNING_REQUEST_STORAGE_KEY,
        JSON.stringify(new WarningRequest({ message, senderId })),
    )

    warningTriggerHandle.isTriggered = true
}

const Warning: FC = () => {
    const [warningRequest, setWarningRequest] = useState(null)

    const resetWarningRequest = () => {
        setWarningRequest(null)
        sessionStorage.setItem(WARNING_REQUEST_STORAGE_KEY, null)
        warningTriggerHandle.isTriggered = false
    }

    useEffect(() => {
        if (warningTriggerHandle.isTriggered) {
            setWarningRequest(JSON.parse(sessionStorage.getItem(WARNING_REQUEST_STORAGE_KEY)))
        }
    }, [warningTriggerHandle.isTriggered])

    const { state: warningState, dispatchAction: dispatchWarningAction } =
        useContext(WarningContext)

    const { show, message } = warningRequest || warningState

    const hideWarning = () => dispatchWarningAction(new HideWarningAction())

    // Comment: Hide warning after 5 seconds
    useEffect(() => {
        if (show) {
            setTimeout(() => {
                if (warningRequest) resetWarningRequest()
                hideWarning()
            }, WARNING_TIMEOUT)
        }
    }, [show])

    return (
        <Container className={cx(show && 'show-warning')}>
            <Message>
                {/* @ts-ignore */}
                <FontAwesomeIcon icon={faInfoCircle} className="warning-info-icon" /> WARNING:{' '}
                {message}
            </Message>

            <button
                type="button"
                className="warning-close-icon"
                onClick={hideWarning}
                aria-label="Close"
            >
                {/* @ts-ignore */}
                <FontAwesomeIcon icon={faTimes} />
            </button>
        </Container>
    )
}

export { dispatchWarningRequest }
export default Warning
